import React from 'react';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';

const IndustriesSliderSection = ({ industriesData }) => {
    const [sliderRef, instanceRef] = useKeenSlider(
        {
            initial: 0,
            loop: true,
            slides: {
                perView: 1,
                spacing: 24,
            },
            breakpoints: {
                '(min-width: 1024px)': {
                    slides: {
                        perView: 2,
                        spacing: 32,
                    },
                },
            },
        },
        [
            (slider) => {
                let timeout;
                let mouseOver = false;

                function clearNextTimeout() {
                    clearTimeout(timeout);
                }

                function nextTimeout() {
                    clearTimeout(timeout);

                    if (mouseOver) return;

                    timeout = setTimeout(() => {
                        slider.next();
                    }, 4000);
                }

                slider.on('created', () => {
                    slider.container.addEventListener('mouseover', () => {
                        mouseOver = true;
                        clearNextTimeout();
                    });

                    slider.container.addEventListener('mouseout', () => {
                        mouseOver = false;
                        nextTimeout();
                    });

                    nextTimeout();
                });

                slider.on('animationEnded', nextTimeout);
                slider.on('updated', nextTimeout);
            },
        ]
    );

    return (
        <section className="w-full bg-[#FFFFFF] py-[80px] lg:py-[100px] select-none overflow-hidden relative z-10 text-left">
            <div className="max-w-[1440px] w-full mx-auto px-5 sm:px-10 lg:px-[80px] flex flex-col gap-12">

                {/* Header */}
                <div className="flex justify-between items-center">
                    <h2 className="text-[#01386A] font-sora font-[600] text-[20px] lg:text-[30px] tracking-tight">
                        Our Industries
                    </h2>

                    <div className="flex items-center gap-4 mt-4 justify-start">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                instanceRef.current?.prev();
                            }}
                            className="w-[32px] h-[32px] rounded-full border border-[#EA6B24] flex items-center justify-center text-[#EA6B24] bg-transparent hover:bg-[#EA6B24] hover:text-white transition-all duration-300 cursor-pointer outline-none"
                        >
                            <ArrowLeft size={18} strokeWidth={2.5} />
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                instanceRef.current?.next();
                            }}
                            className="w-[32px] h-[32px] rounded-full border border-[#EA6B24] flex items-center justify-center text-[#EA6B24] bg-transparent hover:bg-[#EA6B24] hover:text-white transition-all duration-300 cursor-pointer outline-none"
                        >
                            <ArrowRight size={18} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                {/* Slider */}
                <div className="relative w-full">
                    <div
                        ref={sliderRef}
                        className="keen-slider overflow-visible"
                    >
                        {industriesData.map((item) => {
                            return (
                                <div
                                    key={item.id}
                                    className="keen-slider__slide py-2"
                                >
                                    {/* Card */}
                                    <div className="w-full grid grid-cols-1 sm:grid-cols-2 border border-[#BAC9D7] bg-[#FFFFFF] overflow-hidden group min-h-[400px] relative">

                                        {/* Image */}
                                        <div className="w-full h-[220px] sm:h-full overflow-hidden relative">
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="w-full h-full object-cover object-center transform transition-transform duration-700 ease-out group-hover:scale-105"
                                            />
                                        </div>

                                        {/* Content */}
                                        <div className="w-full p-6 sm:p-8 flex flex-col justify-between text-left bg-white relative">

                                            <div className="w-full">

                                                {/* Title */}
                                                <h3 className="text-[#01386A] font-sora font-[600] text-[16px] sm:text-[20px] leading-tight mb-3">
                                                    {item.title}
                                                </h3>

                                                {/* Description */}
                                                <div className="w-full overflow-hidden max-h-0 opacity-0 group-hover:opacity-100 group-hover:max-h-[260px] transition-all duration-500 ease-in-out">
                                                    <p className="text-[#49577A] font-graphik text-[14px] sm:text-[16px] leading-relaxed font-[500] pt-2">
                                                        {item.desc}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Read More */}
                                            <div className="w-full mt-auto pt-4 overflow-hidden max-h-[50px] opacity-100 group-hover:opacity-0 group-hover:max-h-0 transition-all duration-400 ease-in-out">
                                                <div className="flex items-center gap-1.5 cursor-pointer">
                                                    <span className="text-[#01386A] font-graphik font-[500] lg:text-[16px] text-[14px] hover:underline">
                                                        Read More
                                                    </span>

                                                    <ChevronRight
                                                        size={16}
                                                        className="text-[#ED6800]"
                                                    />
                                                </div>
                                            </div>

                                        </div>

                                        {/* Bottom Orange Border Animation */}
                                        <div className="absolute left-0 bottom-0 h-[3px] w-0 bg-[#EA6B24] transition-all duration-500 ease-out group-hover:w-full z-20" />

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>
        </section>
    );
};

export default IndustriesSliderSection;